import { useMemo, useState } from 'react';

/** Search appears once a list reaches this length — unless alwaysSearch is set. */
export const PARTICIPANT_TABLE_SEARCH_THRESHOLD = 10;

/** Rows per page — pagination controls appear above this count. */
export const PARTICIPANT_TABLE_PAGE_SIZE = 12;

export interface UseParticipantTableOptions<T> {
  items: T[];
  remoteTotal?: number;
  getSearchText: (item: T) => string;
  /** Show search once the list reaches this length. Ignored when alwaysSearch is true. */
  searchThreshold?: number;
  /** Organizer dashboard always shows search, even for short lists. */
  alwaysSearch?: boolean;
  /** Paginate the filtered list. Omit to show every row. */
  pageSize?: number;
  /** Viewport-fit cap — paginates only when the filtered list exceeds this. */
  maxPageSize?: number;
  /** Keep maxPageSize rows even when the list fits — pads with placeholders for a full-page table. */
  alwaysUseMaxPageSize?: boolean;
  /** Applied after search — e.g. organizer sort with unassigned first. */
  sortItems?: (items: T[]) => T[];
}

export interface UseParticipantTableResult<T> {
  query: string;
  setSearch: (value: string) => void;
  showSearch: boolean;
  /** Filtered and sorted items for the current page. */
  visible: T[];
  /** Filtered and sorted items across all pages. */
  filtered: T[];
  isEmpty: boolean;
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
  /** Active page size — undefined when every row fits on one page. */
  pageSize: number | undefined;
  /** Invisible rows to pad the last page so pagination stays put. */
  placeholderCount: number;
}

export function useParticipantTable<T>({
  items,
  remoteTotal,
  getSearchText,
  searchThreshold = PARTICIPANT_TABLE_SEARCH_THRESHOLD,
  alwaysSearch = false,
  pageSize: pageSizeOption,
  maxPageSize,
  alwaysUseMaxPageSize = false,
  sortItems,
}: UseParticipantTableOptions<T>): UseParticipantTableResult<T> {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const showSearch = alwaysSearch || items.length > searchThreshold;

  const filtered = useMemo(() => {
    let result = items;
    const needle = query.trim().toLowerCase();
    if (remoteTotal === undefined && showSearch && needle) {
      result = result.filter((item) => getSearchText(item).toLowerCase().includes(needle));
    }
    return sortItems ? sortItems(result) : result;
  }, [getSearchText, items, query, showSearch, sortItems, remoteTotal]);

  const pageSize =
    maxPageSize !== undefined
      ? (remoteTotal ?? filtered.length) > maxPageSize || alwaysUseMaxPageSize
        ? maxPageSize
        : undefined
      : pageSizeOption;

  const pageCount = pageSize ? Math.max(1, Math.ceil((remoteTotal ?? filtered.length) / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount);
  const visible = useMemo(() => {
    if (!pageSize || remoteTotal !== undefined) {
      return filtered;
    }
    return filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [currentPage, filtered, pageSize, remoteTotal]);

  function setSearch(value: string) {
    setQuery(value);
    setPage(1);
  }

  const placeholderCount =
    pageSize && visible.length > 0 && (pageCount > 1 || alwaysUseMaxPageSize)
      ? pageSize - visible.length
      : 0;

  return {
    query,
    setSearch,
    showSearch,
    visible,
    filtered,
    isEmpty: filtered.length === 0,
    page: currentPage,
    pageCount,
    setPage,
    pageSize,
    placeholderCount,
  };
}
